import React, { useState, useEffect, useCallback } from 'react';
import { useError } from '../Contexts/ErrorContext';
import { ajaxErrorHandler } from '../../../utils/ajaxErrorHandler';
import ExportButton from '../ExportButton/ExportButton.jsx';
import ExportService from '../../../services/exportService';
import PanelService from '../../../services/panelService';
import NumberRounding from '../../../utils/numberRounding.js';
import dateFunctions from '../../../utils/dateFunctions.js';
import { PRODUCT_TYPES, CAMPAING_PROMISE_TYPES, CLASSIC_PANEL_STATUS, CAMPAIGN_STATUS } from '../../../constants/enums.js';
import styles from './OutdoorImpact.module.css';
import { useOutdoorImpactData } from './useOutdoorImpactData';
import { outdoorImpactFormatters } from './outdoorImpactFormat';

const OutdoorImpact = ({
    parameters,
    setIsLoading,
    orderlinesData,
    selectedOrderlineIds,
    targetSoV,
    campaignName,
    campaignType,
    campaignPromiseType,
    campaignStatus
}) => {
    // Use hook for core data fetch
    const { oiData, loading: oiLoading, error: oiError } = useOutdoorImpactData(selectedOrderlineIds[0], campaignStatus);
    const [hasUdda, setHasUdda] = useState(false);
    const service = new ExportService();
    const { showError } = useError();
    const [isPlayCampaign, setIsPlayCampaign] = useState(true);

    if (campaignStatus !== CAMPAIGN_STATUS.COMPLETED) {
        return (
            <div className={styles.messageContainer}>
                <div className={styles.messageCard}>
                    <p className={styles.messageText}>
                        This data will be shown after the end of the campaign
                    </p>
                </div>
            </div>
        );
    }

    const calculatedTargetSoV = targetSoV 
        ? NumberRounding.round(targetSoV * 100.0, 2)
        : null;

    // formatters based on user's locale
    const { formatValue } = outdoorImpactFormatters;

    const hideLoading = () => {
        setIsLoading(false);
    }

    useEffect(() => {
        if (campaignType === PRODUCT_TYPES.PLAY ||
            campaignPromiseType === CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS ||
            campaignPromiseType === CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS ||
            campaignPromiseType === CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER) { // lp5 without VAC should not show extra udda labels
            return;
        }
        setHasUdda(orderlinesData.orderLineDataInformation.withUdda);
    }, [orderlinesData, campaignType]);

    useEffect(() => {

        if (campaignType === PRODUCT_TYPES.PLAY)
            setIsPlayCampaign(true);
        else
            setIsPlayCampaign(false);

    }, [campaignType]);

    // Sync external loading flag with hook state (optional)
    useEffect(() => {
        if (campaignStatus !== CAMPAIGN_STATUS.COMPLETED) return;
        setIsLoading(oiLoading);
        if (oiError) showError('Failed to load outdoor impact data. Please try again or contact support.');
    }, [oiLoading, oiError, campaignStatus, setIsLoading, showError]);

    

    const exportIDSResult = useCallback(() => {
        return new Promise((resolve, reject) => {
            service.getIDSExport(
                selectedOrderlineIds[0],
                (data) => resolve(data), // convert to Blob if needed
                (jqXHR, textStatus, errorThrown) => {
                    // The export buttons responsibility to display the error
                    reject(new Error('Failed to fetch outdoor impact Excel. Please try again or contact support.'));
                }
            );
        });
    }, [selectedOrderlineIds]);

    const exportFaceIDResult = useCallback(() => {
        return new Promise((resolve, reject) => {
            try {

                const potentialPanelsToExport =
                    isPlayCampaign ? orderlinesData?.playData?.plays?.panels || [] : [...orderlinesData.postingData, ...orderlinesData.podPostingData];

                if (potentialPanelsToExport.length === 0) {
                    reject(new Error("Failed to export IDS panels to Excel. No panels found."));
                    return;
                }

                if (calculatedTargetSoV === null || calculatedTargetSoV === undefined) {
                    reject(new Error("Failed to export IDS panels to Excel. No target value found."));
                    return;
                }

                if (!parameters || parameters.playSuccessLimit === undefined || parameters.soVNumberOfDecimals === undefined) {
                    reject(new Error("Failed to export IDS panels to Excel. No parameter values found."));
                    return;
                }

                let data;
                if (isPlayCampaign) {
                    const panelExport = PanelService.getIDSPanelsThatMatchesTargetSoV(
                        calculatedTargetSoV,
                        parameters.playSuccessLimit / 100.0,
                        parameters.soVNumberOfDecimals,
                        potentialPanelsToExport,
                        true
                    );

                    if (!panelExport || panelExport.length === 0) {
                        reject(new Error("No panels match the target SoV criteria."));
                        return;
                    }

                    data = panelExport.map((panel) => ({
                        faceid: `${panel.externalKey}`
                    }));
                }
                else {

                    const sortedData = potentialPanelsToExport.sort((a, b) => {
                        const dateA = new Date(a.postingDate);
                        const dateB = new Date(b.postingDate);
                        return dateA - dateB;
                    });

                    data = transformClassicPanelsForExport(sortedData);
                }

                resolve(data);
            }
            catch (error) {
                reject(new Error("Failed to IDS panels to Excel. An unforeseen error occurred. Please contact support."));
            }
        });
    }, [targetSoV, parameters, orderlinesData, isPlayCampaign]);


    const transformClassicPanelsForExport = (data) => {
        return data.reduce((acc, item) => {
            const date = new Date(item.postingDate);
            const weekNumber = dateFunctions.calculateWeek(date);

            const externalKeys = item.panels
                .filter(panel => panel.status.toLowerCase() === CLASSIC_PANEL_STATUS.POSTED && panel.externalKey)
                .map(panel => ({ faceid: panel.externalKey }));

            if (externalKeys.length > 0) {
                acc.push({
                    name: "week " + weekNumber.toString(),
                    data: externalKeys
                });
            }

            return acc;
        }, []);
    };

    const exportIDSSpotplanResult = useCallback((spotlength, soV) => {
        return new Promise((resolve, reject) => {
            if (spotlength === null || spotlength === undefined ||
                soV === null || soV === undefined) {
                reject(new Error('Failed to export spotplan to Excel. Missing values for export. Please contact support.'));
                return;
            }

            service.getSpotplan(
                spotlength,
                soV,
                (data) => resolve(data), // convert to Blob if needed
                (jqXHR, textStatus, errorThrown) => {
                    // The export buttons responsibility to display the error
                    reject(new Error('Failed to export spotplan to Excel. Please try again or contact support.'));
                }
            );
        });
    }, [service]);
    return (
        <div className={styles.oiContainer}>
            {oiData && oiData.data && (
                <div>
                    <div className={styles.contentWrapper}>
                        <div className={styles.containerExportIDSResultButton}>
                            <ExportButton
                                buttonClassName={styles.exportIDSResultButton}
                                fetchData={exportIDSResult}
                                fileNamePrefix={"PoP-IDS-Export-"}
                                buttonCaption={isPlayCampaign ? "IDS result export*" : "IDS result export"}
                                isFileDownload={true}
                                exportType="excel"
                            >
                            </ExportButton>
                        </div>
                        <div className={styles.extraData}>
                            Total faces: {formatValue(oiData.totalFaces)}
                        </div>
                        <div className={styles.extraData}>
                            Face states:: {formatValue(oiData.faceStates)}
                        </div>                       
                        <div className={styles.extraData}>
                            Population: {formatValue(oiData.population)}
                        </div>
                        <br/>
                        <div className={styles.extraData}>
                            {oiData.valuesByDistribution}
                        </div>
                        <table className={styles.oiTable}>
                            <thead>
                                <tr>
                                    {oiData.data[0].map((header, index) => (
                                        <th key={index} className={styles.oiHeader}>
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {oiData.data.slice(1).map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <td key={cellIndex} className={styles.oiCell}>
                                                {formatValue(cell, oiData.data[0][cellIndex])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {isPlayCampaign &&
                        <>
                            <div className={styles.footerNote}>
                                * Spot plan: {oiData.spotPlan}
                            </div>
                            <div className={styles.footerNote}>
                                ** Only including panels with {parameters.playSuccessLimit}% delivery and face id that exists in IDS
                            </div>
                        </>
                        }
                        {!isPlayCampaign && hasUdda &&
                            <div className={styles.footerNote}>
                                VAC numbers are calculated based on a full week
                            </div>
                        }
                    </div>
                    <div className={styles.containerExportFaceIdButton}>
                        <ExportButton
                            buttonClassName={styles.exportFaceIdButton}
                            buttonCaption={"Face ID list to IDS**"}
                            fetchData={exportFaceIDResult}
                            fileNamePrefix={'PoP-IDS-Faces-' + `${campaignName}` + '-'}
                            isFileDownload={false}
                            exportType="excel"
                        >
                        </ExportButton>
                    </div>
                    {isPlayCampaign &&
                        <div className={styles.containerExportSpotplanButton}>
                            <ExportButton
                                buttonClassName={styles.exportSpotplanButton}
                                buttonCaption={"Spotplan to IDS*"}
                                fetchData={() => exportIDSSpotplanResult(
                                    parameters.defaultSpotLength,
                                    calculatedTargetSoV
                                )}
                                fileNamePrefix={'PoP-IDS-Spotplan-' + `${campaignName}` + '-'}
                                isFileDownload={true}
                                exportType="excel"
                            >
                            </ExportButton>
                        </div>
                    }
                </div>
            )}
        </div>
    );
}

export default OutdoorImpact;